import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { fetchProjectAccess, type ProjectAccess } from '../../lib/api/projectPermissions';
import { queryKeys } from './queryKeys';

/**
 * 프로젝트 access 쿼리의 단일 옵션 소스.
 *
 * 같은 엔드포인트를 훅(`useProjectPermissions`)과 명령형 호출(`fetchQuery`, prefetch)이 함께 쓴다.
 * 옵션을 호출부마다 적으면 `retry` 같은 결정이 한쪽에서만 조용히 뒤집힌다 — 그래서 여기 한 곳에만 둔다.
 */
export const isProjectAccessQueryEnabled = (token: string | null, projectId: string): boolean =>
  Boolean(token) && projectId.length > 0;

/**
 * 서버가 접근 상실을 **확정**했는지. 403·404만 여기에 해당한다.
 *
 * 타임아웃·5xx·네트워크 단절은 같은 신호가 아니라 일시 장애다. 이 쿼리는 `retry: false`라
 * 한 번의 실패가 곧 판정이 되므로, 둘을 섞으면 정상 권한 사용자의 화면이 재검증 실패 한 번에
 * 접근 불가 안내로 밀려난다. 판정 규칙을 호출부마다 적으면 훅과 동기화 경로가 갈리므로 여기 한 곳에 둔다.
 */
export const isProjectAccessDeniedError = (error: unknown): boolean => {
  const statusCode = (error as { statusCode?: number } | null | undefined)?.statusCode;
  return statusCode === 403 || statusCode === 404;
};

export const projectAccessQueryOptions = (token: string | null, projectId: string) =>
  queryOptions({
    queryKey: queryKeys.projects.access(projectId),
    queryFn: () => {
      // fetchQuery는 `enabled`를 보지 않으므로 여기서 막는다. 호출부는 실패를 잡아 기본 목적지로 되돌린다.
      if (!token) throw new Error('Project access query requires an auth token');
      return fetchProjectAccess(token, projectId);
    },
    enabled: isProjectAccessQueryEnabled(token, projectId),
    // 권한 없는 프로젝트에 대한 403은 정상 응답이다. 재시도하면 접근 불가 화면이 늦게 뜨고,
    // 이동 직전 선조회에서는 재시도 백오프만큼 클릭이 먹통처럼 보인다.
    retry: false,
    staleTime: 30_000,
  });

/**
 * 이미 받아둔 access. 없으면 `undefined`.
 *
 * 동기 렌더(목록의 `<Link href>` 등)에서 "권한을 안다"와 "아직 모른다"를 구분하는 데 쓴다.
 */
export const readProjectAccessCache = (client: QueryClient, projectId: string): ProjectAccess | undefined =>
  projectId.length > 0 ? client.getQueryData<ProjectAccess>(queryKeys.projects.access(projectId)) : undefined;

/** 캐시가 신선하면 그대로, 아니면 서버에서 받아온다(훅과 동일한 계약). */
export const fetchProjectAccessCached = (
  client: QueryClient,
  token: string | null,
  projectId: string,
): Promise<ProjectAccess> => client.fetchQuery(projectAccessQueryOptions(token, projectId));
